import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Link } from "react-router-dom";
import { Send } from "lucide-react";

export default function ContactForm({ compact = false }) {
  const [form, setForm] = useState({ name: "", email: "", subject: "", category: "Allgemein", message: "" });
  const [consent, setConsent] = useState(true);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!consent) { toast.error("Bitte stimmen Sie der Datenschutzerklärung zu."); return; }
    if (!form.message.trim()) { toast.error("Bitte geben Sie eine Nachricht ein."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/contacts", { ...form, consent });
      setDone(true);
      toast.success(data.message || "Vielen Dank. Ihr Anliegen wurde übermittelt.");
      setForm({ name: "", email: "", subject: "", category: "Allgemein", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Übermittlung fehlgeschlagen.");
    } finally { setLoading(false); }
  }

  if (done) {
    return (
      <div className="bg-[hsl(var(--brand-soft))] border border-[hsl(var(--brand-green))] p-6 sm:p-8 text-center" data-testid="contact-success">
        <h3 className="font-semibold text-xl mb-2">Vielen Dank!</h3>
        <p className="text-sm text-neutral-700 mb-4">Ihr Anliegen wurde übermittelt. Die Stadt Detmold meldet sich bei Ihnen, sofern eine Rückmeldung erforderlich ist.</p>
        <button onClick={() => setDone(false)} className="text-sm text-[hsl(var(--brand-green-strong))] underline">Weiteres Anliegen senden</button>
      </div>
    );
  }

  const containerClass = compact
    ? "bg-white border border-brand-line p-6 sm:p-8"
    : "bg-white border border-brand-line p-8 sm:p-10 lg:p-12";

  return (
    <form onSubmit={submit} className={containerClass} data-testid="contact-form">
      {!compact && (
        <>
          <h2 className="font-semibold text-2xl sm:text-3xl mb-2">Ihr Anliegen an die Stadt</h2>
          <p className="text-sm text-neutral-600 mb-8 max-w-2xl">
            Haben Sie Fragen, Anregungen oder Hinweise zum Bauvorhaben? Schreiben Sie uns – wir nehmen Ihr Anliegen entgegen und melden uns bei Bedarf zurück.
          </p>
        </>
      )}
      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <Field label="Name *">
          <input
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="contact-name"
            className="w-full bg-white border border-brand-line px-4 py-3 text-sm focus:outline-none focus:border-[hsl(var(--brand-green-strong))] focus:ring-1 focus:ring-[hsl(var(--brand-green-strong))]"
          />
        </Field>
        <Field label="E-Mail *">
          <input
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="contact-email"
            className="w-full bg-white border border-brand-line px-4 py-3 text-sm focus:outline-none focus:border-[hsl(var(--brand-green-strong))] focus:ring-1 focus:ring-[hsl(var(--brand-green-strong))]"
          />
        </Field>
      </div>
      <Field label="Betreff">
        <input
          value={form.subject}
          onChange={(e) => setForm({ ...form, subject: e.target.value })}
          data-testid="contact-subject"
          className="w-full bg-white border border-brand-line px-4 py-3 text-sm focus:outline-none focus:border-[hsl(var(--brand-green-strong))] focus:ring-1 focus:ring-[hsl(var(--brand-green-strong))] mb-4"
        />
      </Field>
      <Field label="Kategorie">
        <select
          value={form.category}
          onChange={(e) => setForm({ ...form, category: e.target.value })}
          data-testid="contact-category"
          className="w-full bg-white border border-brand-line px-4 py-3 text-sm focus:outline-none focus:border-[hsl(var(--brand-green-strong))] focus:ring-1 focus:ring-[hsl(var(--brand-green-strong))] mb-4"
        >
          <option value="Allgemein">Allgemein</option>
          <option value="Verkehr">Verkehr</option>
          <option value="Bauphase">Bauphase</option>
          <option value="Beteiligung">Beteiligung</option>
        </select>
      </Field>
      <Field label="Nachricht *">
        <textarea
          required
          rows={6}
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          data-testid="contact-message"
          className="w-full bg-white border border-brand-line px-4 py-3 text-sm focus:outline-none focus:border-[hsl(var(--brand-green-strong))] focus:ring-1 focus:ring-[hsl(var(--brand-green-strong))] mb-4 min-h-[140px]"
        />
      </Field>
      <label className="flex items-start gap-2 text-xs text-neutral-600 mb-5 cursor-pointer">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="contact-consent" className="mt-0.5 accent-[hsl(var(--brand-green-strong))]" />
        <span>
          Ich habe die <Link to="/datenschutz" className="text-[hsl(var(--brand-green-strong))] underline hover:no-underline">Datenschutzerklärung</Link> gelesen und bin mit der Verarbeitung meiner Daten einverstanden.
        </span>
      </label>
      <button
        type="submit"
        disabled={loading}
        data-testid="contact-submit"
        className="inline-flex items-center gap-2 bg-[hsl(var(--brand-green-dark))] text-white px-7 py-3 text-sm font-medium hover:bg-[hsl(var(--brand-green-strong))] transition-colors disabled:opacity-50 rounded-full"
      >
        {loading ? "Wird gesendet…" : (<>Anliegen senden <Send className="h-4 w-4" /></>)}
      </button>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs text-neutral-600 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
